/* eslint-disable max-classes-per-file */
import { chunk, isString } from 'lodash/fp';

const API_VERSION_HEADER = 'x-api-version';

export const FILTER_ABSOLUTE_VALUE_GREATER_THAN_OR_EQUAL = `absgteq`;
export const FILTER_ABSOLUTE_VALUE_LESS_THAN_OR_EQUAL = `abslteq`;
export const FILTER_EQUAL = `eq`;
export const FILTER_GREATER_THAN = `gt`;
export const FILTER_GREATER_THAN_OR_EQUAL = `gteq`;
export const FILTER_IN = `in`;
export const FILTER_IS_NULL = `isnull`;
export const FILTER_LESS_THAN = `lt`;
export const FILTER_LESS_THAN_OR_EQUAL = `lteq`;

/**
 * Extracts our custom 'API version header' from axios headers object
 * @param {object} headers
 * @return {string}
 */
export function getApiVersionHeader(headers) {
	return headers?.[API_VERSION_HEADER] || '';
}

/**
 * Converts float to BE minor units
 * @param {number} value
 * @return {number}
 */
export function formatMinorUnits(value, unFloor = false) {
	if (unFloor) {
		return value * 100;
	}

	return Math.floor(value * 100);
}

/**
 * Converts minor units from BE to float
 * @param {number} amount
 * @return {number}
 */
export function parseMinorUnits(amount) {
	return amount / 100;
}

/**
 * Formattes phone number
 * @param {PhoneNumber} phoneNumber
 * @param {object}  options
 * @property {boolean} spacedTriples format phone number as three triples
 * delimited by nbsp
 * @example 777555333 => 777 555 333
 * @property {bool} omitCountryCode omits national prefix while printing
 * @return {string}
 */
export const formatPhoneNumber = (
	{ countryCode, number } = {},
	{ spacedTriples, omitCountryCode } = {}
) => {
	if (!number) return '';

	let extractedPlus = '';
	let cleanNumber = number;

	if (number.startsWith('+')) {
		extractedPlus = '+';
		cleanNumber = number.replace(/^\+/, '');
	}

	if (countryCode) {
		const codeDigits = countryCode.replace('+', '');
		if (cleanNumber.startsWith(codeDigits)) {
			cleanNumber = cleanNumber.slice(codeDigits.length);
		}
	}

	const formattedNumber = spacedTriples
		? chunk(3, cleanNumber)
				.map((t) => t.join(''))
				.join('\xA0')
		: cleanNumber;

	if (omitCountryCode) return extractedPlus + formattedNumber;

	const prefix = countryCode || extractedPlus;
	const sep = spacedTriples && prefix && prefix !== '+' ? '\xA0' : '';

	return `${prefix}${sep}${formattedNumber}`;
};

/**
 * Converts Uint8Array to string.
 * @param {Uint8Array} u8a
 * @return {string}
 */
function uint8ToString(u8a) {
	const CHUNK_SZ = 0x8000;
	const c = [];
	for (let i = 0; i < u8a.length; i += CHUNK_SZ) {
		c.push(String.fromCharCode.apply(null, u8a.subarray(i, i + CHUNK_SZ)));
	}
	return c.join('');
}

/**
 * Exports File object from File DOM interface
 * @param {File} file
 * @see https://developer.mozilla.org/en-US/docs/Web/API/File
 * @return {Promise<{ content: BinaryType, filename: string }>}
 */
export function getFileObject(file) {
	return new Promise((resolve, reject) => {
		const fileReader = new window.FileReader();
		fileReader.onerror = reject;
		fileReader.onload = () => {
			const bytes = new Uint8Array(fileReader.result);
			const str = uint8ToString(bytes);
			const content = window.btoa(str);
			resolve({ content, filename: file.name });
		};
		fileReader.readAsArrayBuffer(file);
	});
}

/**
 * Constructs BankStatement from File schema object
 * @param file
 */
export function toBankStatementDocument(file) {
	return {
		file,
		type: 'BANK_STATEMENT',
	};
}

export function cleanFulltextSearch(value) {
	return value.replace(/%00/g, '');
}

/**
 * Class with seamless interface to define query filter for endpoints
 *
 * @example
 *
 * 	const endpointQueryParams = {
 * 		filter: new QueryFilter().prop('id').eq(123)
 * 	}
 *
 * @example complex case
 *
 * 	const endpointQueryParams = {
 * 		filter: new QueryFilter()
 * 			.prop('movie.rating').gteq(4)
 * 			.prop('createdAt').gteq('2018-01-01').lteq('2018-12-31')
 * 	}
 */
export class QueryFilter {
	static joinCondition(path, operator, value) {
		return `${path}|${operator}|${encodeURIComponent(value)}`;
	}

	constructor() {
		this.conditions = [];
		this.currentPath = null;
	}

	absgteq(value) {
		return this.addCondition(this.currentPath, FILTER_ABSOLUTE_VALUE_GREATER_THAN_OR_EQUAL, value);
	}

	abslteq(value) {
		return this.addCondition(this.currentPath, FILTER_ABSOLUTE_VALUE_LESS_THAN_OR_EQUAL, value);
	}

	addCondition(path, operator, value) {
		const newInstance = new QueryFilter();
		newInstance.conditions = this.conditions.concat(
			QueryFilter.joinCondition(path, operator, value)
		);
		newInstance.currentPath = this.currentPath;
		return newInstance;
	}

	eq(value) {
		return this.addCondition(this.currentPath, FILTER_EQUAL, value);
	}

	fulltext(value) {
		return this.addCondition('fulltext', 'icontains', cleanFulltextSearch(value));
	}

	gt(value) {
		return this.addCondition(this.currentPath, FILTER_GREATER_THAN, value);
	}

	gteq(value) {
		return this.addCondition(this.currentPath, FILTER_GREATER_THAN_OR_EQUAL, value);
	}

	in(values) {
		return this.addCondition(this.currentPath, FILTER_IN, values.join(','));
	}

	isnull(value = true) {
		return this.addCondition(this.currentPath, FILTER_IS_NULL, value ? 'true' : 'false');
	}

	lt(value) {
		return this.addCondition(this.currentPath, FILTER_LESS_THAN, value);
	}

	lteq(value) {
		return this.addCondition(this.currentPath, FILTER_LESS_THAN_OR_EQUAL, value);
	}

	// eslint-disable-next-line class-methods-use-this
	prop(path) {
		const newInstance = new QueryFilter();
		newInstance.conditions = this.conditions;
		newInstance.currentPath = path;
		return newInstance;
	}

	toString() {
		return `${this.conditions.join(';')}`;
	}

	valueOf() {
		return this.toString();
	}

	merge(queryFilter) {
		const merged = new QueryFilter();

		if (queryFilter instanceof QueryFilter) {
			merged.conditions = this.conditions.concat(queryFilter.conditions);
		} else if (isString(queryFilter)) {
			merged.conditions = this.conditions.concat(queryFilter);
		} else {
			throw new TypeError(
				`Argument "queryFilter" of "QueryFilter.prototype.merge" must be either instance of QueryFilter or a string. Received "${JSON.stringify(
					queryFilter
				)}"`
			);
		}
		return merged;
	}

	static fromString(filterString) {
		const newInstance = new QueryFilter();
		if (filterString && typeof filterString === 'string') {
			newInstance.conditions = filterString.split(';');
		}
		return newInstance;
	}
}

/**
 * Class with seamless interface to define query sort for endpoints
 *
 * @example
 *
 * 	const endpointQueryParams = {
 * 		sort: new QuerySort().by('id').descendingBy('date')
 * 	}
 *
 */
export class QuerySort {
	constructor() {
		this.attributes = [];
	}

	addRule(attribute) {
		const newInstance = new QuerySort();
		newInstance.attributes = this.attributes.concat(attribute);
		return newInstance;
	}

	/**
	 * Sets attribute to sort in ascending order
	 * @param {string} attribute
	 * @return {QuerySort}
	 */
	by(attribute) {
		return this.addRule(attribute);
	}

	/**
	 * Sets attribute to sort in descending order
	 * @param {string} attribute
	 * @return {QuerySort}
	 */
	descendingBy(attribute) {
		return this.addRule(`-${attribute}`);
	}

	toString() {
		return `${this.attributes.join(',')}`;
	}

	valueOf() {
		return this.toString();
	}
}

export function filterStringToValues(filterString) {
	if (!filterString) {
		return undefined;
	}
	const filter = filterString.split(';');
	const values = {};
	filter.forEach((filterItemString) => {
		const filterItem = filterItemString.split('|');
		const [filterName, , filterValue] = filterItem;
		values[filterName] = decodeURIComponent(filterValue);
	});
	return values;
}
